// ============================================================================
// Shadow Network — Editar Perfil (REWRITTEN FROM SCRATCH)
// Route: /shadow/perfil/editar
//
// Simple, defensive, and production-safe:
//   • Loads existing profile data via GET /api/shadow/social/profile/me.
//   • If GET fails, seeds the form from useAuth().user so it's NEVER empty.
//   • PATCH /api/shadow/social/profile/me only sends fields that actually
//     changed, so we can't accidentally wipe the profile on a network blip.
//   • Save button is disabled until the user has a non-empty @username.
//   • After save, redirects to /shadow/perfil/<username> (their public page).
//   • Avatar/banner uploads kept intact — they already work in production.
// ============================================================================
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Camera, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { UserAvatar } from "../components/shadow/UserAvatar";
import { ImageCropperModal } from "../components/shadow/ImageCropperModal";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { CopyProfileUrlButton } from "../components/shadow/CopyProfileUrlButton";
// Runtime-first API base: in production (*.rebelbtc.com) this resolves to
// window.location.origin so the requests stay same-origin (Vercel rewrites
// /api/* to the Emergent backend). In preview it falls back to
// REACT_APP_BACKEND_URL. See /lib/api.js.
import { API_BASE as API } from "../lib/api";

const TRADING_STYLES = ["scalper", "swing", "position", "investor"];
const SPECIALTIES = ["BTC", "ETH", "Altcoins", "Multi"];
const USERNAME_RE = /^[a-z0-9_]{3,20}$/;

// FIX BUG 1: Resize image blob to target dimensions before upload.
// Used for banners (1500x500) to avoid Cloudflare HTTP 520 (payload too large)
// and to keep Cloudinary transformations stable.
const resizeImageBlob = (sourceBlob, targetW, targetH) =>
  new Promise((resolve, reject) => {
    if (!sourceBlob) return reject(new Error("no_source_blob"));
    const url = URL.createObjectURL(sourceBlob);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext("2d");
        // Cover: scale so the source covers the target box, center-crop.
        const srcRatio = img.width / img.height;
        const dstRatio = targetW / targetH;
        let sx = 0, sy = 0, sw = img.width, sh = img.height;
        if (srcRatio > dstRatio) {
          // source wider than target — crop sides
          sw = img.height * dstRatio;
          sx = (img.width - sw) / 2;
        } else if (srcRatio < dstRatio) {
          // source taller than target — crop top/bottom
          sh = img.width / dstRatio;
          sy = (img.height - sh) / 2;
        }
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetW, targetH);
        canvas.toBlob(
          (out) => {
            URL.revokeObjectURL(url);
            if (out) resolve(out);
            else reject(new Error("toBlob_failed"));
          },
          "image/webp",
          0.85
        );
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });

// Map any user object (auth or full profile) to our flat form shape.
const seedFromUser = (u = {}) => ({
  username: u.username || "",
  display_name: u.name || u.display_name || "",
  bio: u.bio || "",
  location: u.location || "",
  website: u.website || "",
  twitter_handle: u.twitter_handle || "",
  trading_style: u.trading_style || "",
  specialty: u.specialty || "",
  tip_address: u.tip_address || "",
  tip_chain: u.tip_chain || "base",
});

const ShadowProfileEditPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [initialForm, setInitialForm] = useState(seedFromUser({}));
  const [form, setForm] = useState(seedFromUser({}));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Avatar & banner cropping state (kept from the previous working version).
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [cropState, setCropState] = useState({ file: null, kind: null });
  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  // -------- Hydrate form on mount --------
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    let alive = true;
    // 1) Seed immediately from auth user so the form is never blank.
    const seedFromAuth = seedFromUser(user);
    setForm(seedFromAuth);
    setInitialForm(seedFromAuth);
    // 2) Fetch the canonical profile to overwrite with fresh values.
    axios
      .get(`${API}/api/shadow/social/profile/me`)
      .then(({ data }) => {
        if (!alive) return;
        setProfile(data);
        const fresh = seedFromUser(data.user || {});
        setForm(fresh);
        setInitialForm(fresh);
      })
      .catch((err) => {
        if (!alive) return;
        // Non-fatal — we keep the auth-seeded form. Surface a soft warning.
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          setError("Sesión caducada — vuelve a iniciar sesión.");
        } else if (!status) {
          // Network error — don't block editing. User can still save.
          // eslint-disable-next-line no-console
          console.warn("[edit-profile] could not refresh profile data:", err);
        } else {
          // eslint-disable-next-line no-console
          console.warn(`[edit-profile] /me returned HTTP ${status}`);
        }
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [user]);

  // -------- Avatar / banner upload (kept as-is, this works in prod) --------
  const upload = async (file, kind) => {
    if (!file) return { ok: false };
    const setter = kind === "avatar" ? setAvatarUploading : setBannerUploading;
    setter(true);
    setError("");
    try {
      // FIX BUG 1: Si es banner, redimensionar a 1500x500 WebP antes de subir.
      // Esto evita HTTP 520 de Cloudflare por imagen demasiado grande y normaliza
      // las dimensiones para Cloudinary.
      let uploadable;
      if (kind === "banner") {
        try {
          const resized = await resizeImageBlob(file, 1500, 500);
          if (!resized || resized.size > 5 * 1024 * 1024) {
            const msg = "Imagen demasiado grande. Máximo 5 MB.";
            setError(msg);
            return { ok: false, error: msg };
          }
          uploadable = new File([resized], `banner-${Date.now()}.webp`, {
            type: "image/webp",
          });
        } catch (resizeErr) {
          // Fallback: si el resize falla, usar el blob original
          // eslint-disable-next-line no-console
          console.warn("[edit-profile] banner resize failed, using original:", resizeErr);
          uploadable =
            file instanceof File
              ? file
              : new File([file], `banner-${Date.now()}.jpg`, {
                  type: file.type || "image/jpeg",
                });
        }
      } else {
        uploadable =
          file instanceof File
            ? file
            : new File([file], `${kind}-${Date.now()}.jpg`, {
                type: file.type || "image/jpeg",
              });
      }
      const fd = new FormData();
      fd.append("file", uploadable);
      const { data } = await axios.post(
        `${API}/api/shadow/social/profile/${kind}`,
        fd
      );
      setProfile((p) => ({
        ...(p || {}),
        user: {
          ...(p?.user || {}),
          ...(kind === "avatar"
            ? { avatar_url: data.avatar_url }
            : { banner_url: data.banner_url }),
        },
      }));
      try {
        if (typeof refreshUser === "function") {
          const r = refreshUser();
          if (r && typeof r.catch === "function") r.catch(() => {});
        }
      } catch { /* non-fatal */ }
      setCropState({ file: null, kind: null });
      return { ok: true };
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg =
        detail ||
        (status === 401 || status === 403
          ? "Sesión caducada. Recarga la página e intenta de nuevo."
          : status === 413
          ? "Imagen demasiado grande (máx 5 MB)."
          : status === 520 || status === 502 || status === 504
          ? "El servidor de imágenes está saturado. Reintenta en unos segundos."
          : !status
          ? "Error de red. Comprueba tu conexión."
          : `Subida fallida (HTTP ${status}).`);
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setter(false);
    }
  };

  const onFilePicked = (file, kind) => {
    if (!file) return;
    setCropState({ file, kind });
    if (kind === "avatar" && avatarInputRef.current) avatarInputRef.current.value = "";
    if (kind === "banner" && bannerInputRef.current) bannerInputRef.current.value = "";
  };

  // -------- SAVE — diff payload, validates username, redirects on success --
  const guardar = async () => {
    setError("");
    setSuccess(false);

    // Username is required and must match the format.
    const un = (form.username || "").trim().toLowerCase();
    if (!un) {
      setError("El @username es obligatorio para guardar.");
      return;
    }
    if (!USERNAME_RE.test(un)) {
      setError("Username inválido. Solo letras, números y _ (3-20 caracteres).");
      return;
    }

    // Build the diff payload — only send fields that actually changed.
    const norm = (v) => (v === "" || v == null ? null : v);
    const candidate = { ...form, username: un };
    const payload = {};
    for (const k of Object.keys(candidate)) {
      if (norm(candidate[k]) !== norm(initialForm[k])) payload[k] = candidate[k];
    }
    // Always include the validated username so the backend can confirm it.
    if (!("username" in payload)) payload.username = un;

    setSaving(true);
    try {
      // FIX BUG 2: Include credentials so the session cookie travels with the
      // PATCH in production. The Bearer token is still auto-attached by the
      // axios interceptor as a fallback.
      const { data } = await axios.patch(
        `${API}/api/shadow/social/profile/me`,
        payload,
        { withCredentials: true }
      );
      const savedUsername = data?.user?.username || un;
      // Update local profile state so the diff baseline reflects what's now
      // on the server (so a second save doesn't re-send the same fields).
      if (data?.user) {
        setProfile(data);
        const fresh = seedFromUser(data.user);
        setInitialForm(fresh);
      }
      // Refresh global auth user so Navbar updates instantly.
      try {
        if (typeof refreshUser === "function") {
          const r = refreshUser();
          if (r && typeof r.catch === "function") r.catch(() => {});
        }
      } catch { /* non-fatal */ }
      setSuccess(true);
      // FIX BUG 3: Redirect to the user's public profile 1s after save using
      // window.location.href (a full-page navigation, NOT SPA navigate). This
      // guarantees no React state crashes between routes and is the simplest
      // possible code path.
      if (savedUsername && typeof savedUsername === "string") {
        setTimeout(() => {
          window.location.href = `/shadow/perfil/${savedUsername}`;
        }, 1000);
      }
    } catch (e) {
      // Show the REAL reason (HTTP code or backend detail), never a silent
      // "save_failed". This is the diagnostic surface for prod issues.
      // eslint-disable-next-line no-console
      console.error("[edit-profile] PATCH failed:", e);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (detail) setError(typeof detail === "string" ? detail : JSON.stringify(detail));
      else if (status === 401 || status === 403)
        setError("Sesión caducada — vuelve a iniciar sesión.");
      else if (status === 409)
        setError("Este @username ya está en uso por otro operador.");
      else if (!status)
        setError("Error de red. Comprueba tu conexión e inténtalo de nuevo.");
      else setError(`No se pudo guardar (HTTP ${status}).`);
    } finally {
      setSaving(false);
    }
  };

  // -------- Guards --------
  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-sm text-white/65 mb-4">Acceso de operador requerido.</p>
          <Link to="/login" className="text-amber-400 underline">
            Iniciar sesión
          </Link>
        </div>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <p className="text-amber-400 text-sm font-mono">Cargando…</p>
      </div>
    );
  }

  const u = profile?.user || user || {};
  const canSave = !!(form.username || "").trim() && !saving;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="profile-edit-page">
      {/* Header */}
      <header className="border-b border-white/5 bg-black/70 backdrop-blur-xl sticky top-0 z-30">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4 md:px-8">
          <Link
            to={u.username ? `/shadow/perfil/${u.username}` : "/shadow"}
            className="inline-flex items-center gap-2 text-white/60 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">Cancelar</span>
          </Link>
          <h1 className="text-base font-bold uppercase tracking-[0.18em]">
            Editar Perfil
          </h1>
          <button
            type="button"
            onClick={guardar}
            disabled={!canSave}
            data-testid="profile-save-btn"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-5 py-1.5 text-xs font-black uppercase tracking-[0.18em] text-black transition-all hover:from-amber-300 hover:to-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving && <Loader2 className="h-3 w-3 animate-spin" />}
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 md:px-8 space-y-6">
        {/* Banner upload */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-6">
          <h2 className="mb-4 text-[10px] font-bold uppercase tracking-[0.25em] text-amber-300">
            Foto de portada
          </h2>
          <div
            className="relative h-40 w-full overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500/15 via-fuchsia-500/10 to-cyan-400/15 md:h-56"
            style={
              u.banner_url
                ? {
                    backgroundImage: `url(${u.banner_url})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }
                : undefined
            }
          >
            <button
              type="button"
              onClick={() => bannerInputRef.current?.click()}
              disabled={bannerUploading}
              data-testid="banner-upload-btn"
              className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white backdrop-blur transition-all hover:bg-black/85 disabled:opacity-50"
            >
              {bannerUploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
              Cambiar
            </button>
            <input
              ref={bannerInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => onFilePicked(e.target.files?.[0], "banner")}
              data-testid="banner-upload-input"
            />
          </div>
        </section>

        {/* Avatar upload */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-6">
          <h2 className="mb-4 text-[10px] font-bold uppercase tracking-[0.25em] text-amber-300">
            Foto de perfil
          </h2>
          <div className="flex items-center gap-4">
            <UserAvatar user={u} size="xl" />
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                disabled={avatarUploading}
                data-testid="avatar-upload-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white hover:border-amber-400/60"
              >
                {avatarUploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
                Subir foto
              </button>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => onFilePicked(e.target.files?.[0], "avatar")}
                data-testid="avatar-upload-input"
              />
              <p className="text-[10px] text-white/35">JPG/PNG/WebP · max 5 MB</p>
            </div>
          </div>
        </section>

        {/* Profile form — simple, clean, all fields visible */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-6 space-y-5">
          <h2 className="text-[10px] font-bold uppercase tracking-[0.25em] text-amber-300">
            Datos del operador
          </h2>

          {/* @username */}
          <div>
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">
              @username (3-20 chars · letras, números, guión bajo) — obligatorio
            </label>
            <div className="flex items-center rounded-xl border border-amber-400/25 bg-black/30 overflow-hidden">
              <span className="px-3 text-amber-400/60 font-mono text-sm border-r border-amber-400/15">@</span>
              <input
                type="text"
                value={form.username}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""),
                  }))
                }
                data-testid="field-username"
                maxLength={20}
                placeholder="tuusername"
                className="w-full bg-transparent px-3 py-2.5 text-sm text-white font-mono focus:outline-none"
              />
            </div>
            {form.username && form.username.length >= 3 && (
              <div className="mt-2 flex items-center gap-2 rounded-lg border border-white/10 bg-black/40 px-3 py-2">
                <span className="truncate font-mono text-[11px] text-white/55">
                  rebelbtc.com/shadow/perfil/<span className="text-amber-300">{form.username}</span>
                </span>
                <div className="ml-auto">
                  <CopyProfileUrlButton username={form.username} variant="icon" />
                </div>
              </div>
            )}
          </div>

          {/* Display name */}
          <div>
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">
              Nombre para mostrar
            </label>
            <input
              type="text"
              value={form.display_name}
              onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
              data-testid="field-display-name"
              maxLength={50}
              placeholder="Tu nombre"
              className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-sm text-white focus:border-amber-400/40 focus:outline-none"
            />
          </div>

          {/* Bio */}
          <div>
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">
              Bio · {(form.bio || "").length}/160
            </label>
            <textarea
              value={form.bio}
              onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value.slice(0, 160) }))}
              data-testid="field-bio"
              rows={3}
              placeholder="Cuéntanos quién eres como trader…"
              className="w-full resize-none rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-sm text-white focus:border-amber-400/40 focus:outline-none"
            />
          </div>

          {/* Trading style */}
          <div>
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">
              Estilo de trading
            </label>
            <div className="flex flex-wrap gap-2">
              {TRADING_STYLES.map((s) => {
                const active = form.trading_style === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, trading_style: active ? "" : s }))}
                    data-testid={`chip-style-${s}`}
                    className={`rounded-full border px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.15em] transition-all ${
                      active
                        ? "border-amber-400/60 bg-amber-400/[0.15] text-amber-200"
                        : "border-white/10 bg-white/[0.03] text-white/55 hover:border-white/25 hover:text-white"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Specialty */}
          <div>
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">
              Especialidad
            </label>
            <div className="flex flex-wrap gap-2">
              {SPECIALTIES.map((s) => {
                const active = form.specialty === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, specialty: active ? "" : s }))}
                    data-testid={`chip-specialty-${s}`}
                    className={`rounded-full border px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.15em] transition-all ${
                      active
                        ? "border-cyan-400/60 bg-cyan-400/[0.12] text-cyan-300"
                        : "border-white/10 bg-white/[0.03] text-white/55 hover:border-white/25 hover:text-white"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">
              Ubicación (opcional)
            </label>
            <input
              type="text"
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              data-testid="field-location"
              placeholder="España, Europa…"
              className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-sm text-white focus:border-amber-400/40 focus:outline-none"
            />
          </div>

          {/* Website */}
          <div>
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">
              Website (opcional)
            </label>
            <input
              type="text"
              value={form.website}
              onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
              data-testid="field-website"
              placeholder="https://…"
              className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-sm text-white focus:border-amber-400/40 focus:outline-none"
            />
          </div>

          {/* Twitter */}
          <div>
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">
              Twitter / X (opcional)
            </label>
            <input
              type="text"
              value={form.twitter_handle}
              onChange={(e) =>
                setForm((f) => ({ ...f, twitter_handle: e.target.value.replace(/^@+/, "") }))
              }
              data-testid="field-twitter"
              placeholder="handle"
              className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-sm text-white focus:border-amber-400/40 focus:outline-none"
            />
          </div>
        </section>

        {/* Status messages */}
        {error && (
          <div
            className="rounded-xl border border-rose-500/30 bg-rose-500/[0.08] px-4 py-3 text-sm text-rose-200"
            data-testid="profile-edit-error"
          >
            ⚠️ {String(error)}
          </div>
        )}
        {success && (
          <div
            className="rounded-xl border border-emerald-400/30 bg-emerald-400/[0.08] px-4 py-3 text-sm text-emerald-200 flex items-center justify-between gap-3 flex-wrap"
            data-testid="profile-edit-success"
          >
            <span>✅ Perfil guardado. Redirigiendo a tu perfil…</span>
            {form.username && (
              <Link
                to={`/shadow/perfil/${form.username}`}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-400/40 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-emerald-300 hover:bg-emerald-400/10"
                data-testid="goto-public-profile"
              >
                Ir ahora →
              </Link>
            )}
          </div>
        )}

        {/* Big Save CTA at the bottom — mirrors the reference design */}
        <button
          type="button"
          onClick={guardar}
          disabled={!canSave}
          data-testid="profile-save-btn-bottom"
          className="w-full rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 px-4 py-3.5 text-sm font-black uppercase tracking-[0.18em] text-black transition-all hover:from-amber-300 hover:to-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Guardando…" : "Guardar Perfil →"}
        </button>
        <p className="text-center text-[11px] text-white/30 mt-1">
          El @username es obligatorio para guardar
        </p>
      </main>

      {/* Cropper modal (avatar 1:1, banner 3:1) */}
      {cropState.file && (
        <ErrorBoundary onReset={() => setCropState({ file: null, kind: null })}>
          <ImageCropperModal
            file={cropState.file}
            kind={cropState.kind}
            busy={cropState.kind === "avatar" ? avatarUploading : bannerUploading}
            uploadError={error}
            onCancel={() => {
              setCropState({ file: null, kind: null });
              setError("");
            }}
            onApply={async (blob) => {
              try {
                return await upload(blob, cropState.kind);
              } catch (e) {
                // eslint-disable-next-line no-console
                console.error("[edit-profile] upload threw:", e);
                return { ok: false, error: (e && e.message) || "upload_failed" };
              }
            }}
          />
        </ErrorBoundary>
      )}
    </div>
  );
};

export default ShadowProfileEditPage;
